import { Box, Paper, TableSortLabel, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { visuallyHidden } from '@mui/utils';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  MenuItemData,
  ThreeDotsMenu,
} from '../../../components/documented/ThreeDotsMenu';
import NoData from '../../../components/NoData';
import PathCircularProgress from '../../../components/PathCircularProgress';
import SearchBar from '../../../components/SearchBar/SearchBar';
import { PathAccount } from '../../../model/account/pathAccount';
import { Role } from '../../../model/account/role';
import { getAllHcpAccounts } from '../../../redux/actions/adminActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { selectAccountId } from '../../../redux/selectors/accountSelectors';
import { selectPatientAccounts } from '../../../redux/selectors/adminSelectors';
import accountService from '../../../services/AccountService';
import {
  copyIdToClipboard,
  getComparator,
  Order,
  StyledTableCell,
  StyledTableRow,
} from '../TableComponents/TableComponents';

const TableDetailContainer = styled(Box)(() => ({
  boxSizing: 'border-box',
  height: 'fit-content',
  width: '100%',
  padding: '10px 40px 10px 20px',
}));

export interface HeadCell {
  id: keyof PathAccount;
  label: string;
  numeric: boolean;
  toolTip?: string;
}

export interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof PathAccount
  ) => void;
  order: Order;
  orderBy: string;
}

function PatientAccountsTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const { t } = useTranslation(['admin', 'common']);
  const createSortHandler =
    (property: keyof PathAccount) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const headCells: readonly HeadCell[] = [
    {
      id: 'accountGuid',
      numeric: false,
      label: t('common:fields.accountId'),
    },
    {
      id: 'created',
      numeric: false,
      label: t('patientAccounts.userSince'),
    },
  ];

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align="left"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <Tooltip title={headCell.toolTip || ''}>
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? t('common:table.sortedDescending')
                      : t('common:table.sortedAscending')}
                  </Box>
                ) : null}
              </TableSortLabel>
            </Tooltip>
          </StyledTableCell>
        ))}
        <StyledTableCell align="left">
          {t('common:table.userActions')}
        </StyledTableCell>
      </TableRow>
    </TableHead>
  );
}

interface Props {
  isLoading: boolean;
}

export const PatientAccountsComponent: React.FC<Props> = ({ isLoading }) => {
  const { t } = useTranslation(['admin', 'common']);
  const accounts = useSelector(selectPatientAccounts);
  const accountId = useSelector(selectAccountId);
  const dispatch = useAppThunkDispatch();
  const navigate = useNavigate();
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof PathAccount>('lastName');
  const [searchTerm, setSearchTerm] = React.useState('');

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const filteredAccounts = React.useMemo(() => {
    try {
      // If no search term, return all accounts
      if (!searchTerm) return accounts;

      // Validate accounts array
      if (!accounts || !Array.isArray(accounts)) {
        return [];
      }

      // Get lowercase search term
      const searchLower = String(searchTerm).toLowerCase();

      // Filter accounts
      return accounts.filter((account) => {
        try {
          if (!account) return false;

          const accountId = account.accountGuid
            ? account.accountGuid.toLowerCase()
            : '';
          const createdDate = account.created
            ? new Date(account.created).toDateString().toLowerCase()
            : '';

          return (
            accountId.includes(searchLower) || createdDate.includes(searchLower)
          );
        } catch (err) {
          console.error('Error filtering account:', err);
          return false;
        }
      });
    } catch (err) {
      console.error('Error in filteredAccounts:', err);
      return [];
    }
  }, [accounts, searchTerm]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof PathAccount
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function handleDeleteAccount(accountGuid: string) {
    console.log('trying to delete account', accountGuid);
    try {
      accountService
        .deleteAccount(accountGuid)
        .then((response: any) => {
          dispatch(getAllHcpAccounts());
        })
        .then((response: any) => {});
    } catch (error: any) {
      //hmmm
    }
  }

  const menuItems: MenuItemData[] = [
    {
      label: t('patientAccounts.seeData'),
      action: (data) => navigate(`/dashboard/patients/${data}`),
    },
    {
      label: t('common:actions.copyGuid'),
      action: (data) => copyIdToClipboard(data),
    },
    {
      label: t('common:actions.remove'),
      action: (data) => handleDeleteAccount(data),
    },
  ];

  return (
    <TableDetailContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
        }}
      >
        <Typography
          sx={{ padding: '5px 0px 5px 0px' }}
          variant="h5"
          component="h2"
        >
          {t('patientAccounts.title')}
        </Typography>
        {!isLoading && accounts.length > 0 && (
          <SearchBar
            placeholder={t('patientAccounts.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        )}
      </Box>
      {!isLoading ? (
        accounts && accounts.length ? (
          filteredAccounts.length > 0 ? (
            <Paper sx={{ width: '100%', mb: 2 }}>
              <TableContainer sx={{ maxHeight: 490, minWidth: 750 }}>
                <Table stickyHeader aria-labelledby="patient-accounts-table">
                  <PatientAccountsTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                  />
                  <TableBody>
                    {filteredAccounts
                      .slice()
                      .sort(getComparator(order, orderBy))
                      .map((account) => {
                        return (
                          <StyledTableRow
                            hover
                            tabIndex={-1}
                            key={account.accountGuid}
                          >
                            <StyledTableCell align="left">
                              {account.accountGuid}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {new Date(account.created).toDateString()}
                            </StyledTableCell>
                            <StyledTableCell>
                              {
                                //cant delete self or super admin account type
                                account.accountGuid !== accountId &&
                                account.role !== Role.SuperAdmin ? (
                                  <ThreeDotsMenu
                                    data={account.accountGuid}
                                    menuItems={menuItems}
                                  />
                                ) : null
                              }
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ) : (
            <NoData
              message={t('patientAccounts.noneMatching', { term: searchTerm })}
            />
          )
        ) : (
          <NoData message={t('patientAccounts.empty')} />
        )
      ) : (
        <Box
          data-testid="loading-indicator" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100px', justifyContent: 'center' }}>
          <PathCircularProgress />
        </Box>
      )}
    </TableDetailContainer>
  );
};

export default PatientAccountsComponent;
